import { useTranslation } from 'react-i18next'
import { useLocaleFormat } from '../../i18n/useLocaleFormat'
import { useSparks } from '../../api/hooks'
import { useAuth } from '../../contexts/AuthContext'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'

const isLocalDev = import.meta.env.DEV &&
  !import.meta.env.VITE_ENABLE_AUTH &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

interface SparksBalanceProps {
  onClick?: () => void
}

export default function SparksBalance({ onClick }: SparksBalanceProps) {
  const { t } = useTranslation('workspace')
  const { formatNumber } = useLocaleFormat()
  const { isAuthenticated } = useAuth()
  // The OPTIONAL variant: this badge renders outside a project too (settings,
  // landing), and `useProjectId()` THROWS without a provider rather than
  // returning undefined. Undefined is the right value there — the endpoint
  // falls back to the actor's own wallet. Inside a project it names the
  // project, so the badge shows the wallet THAT project charges rather than a
  // different one the user happens to own.
  const projectId = useOptionalProjectId()
  const { data, isLoading } = useSparks(projectId)

  if (!isAuthenticated && !isLocalDev) {
    return null
  }

  // THE POOL, not the spendable figure.
  //
  // `balance` is `min(pool, allowance)`, so for a member with a per-member cap
  // this badge used to render their own quota — which reads as the TEAM being
  // nearly out of credits when the team is fine. Showing the wallet's own
  // balance is the honest answer for a persistent header badge; the cap is a
  // property of one member's spending, not of the credits the team holds.
  //
  // The cap is still enforced, and the dialog shown when a run is blocked says
  // so explicitly (see InsufficientSparksModal). `pool` is absent pre-teams and
  // on the debug path, where `balance` IS the wallet.
  const balance = data?.pool ?? data?.balance ?? 0

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="sparks-balance-badge"
      title={t('sparksBalance.creditBalance')}
      // Pill, bolt icon and figure move together — a white bolt inside an
      // amber pill reads as a rendering bug, not a partial restyle.
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-chrome/15 border border-chrome/30 hover:bg-chrome/25 hover:border-chrome/50 transition-colors cursor-pointer"
    >
      <svg
        className="w-4 h-4 text-chrome shrink-0"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
      </svg>
      <span className="text-chrome text-sm font-medium leading-none">
        {isLoading ? '...' : formatNumber(balance)}
      </span>
    </button>
  )
}
